"use client";

import React from "react";
import { CheckCircle, AlertTriangle, Lock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils/cn";

// Main Tabs container
export type TabsType = "breadcrumb" | "tabs";

export interface TabsProps {
  children: React.ReactNode;
  value: string;
  type?: TabsType;
  className?: string;
}

export function Tabs({ children, className = "" }: TabsProps) {
  return <div className={className}>{children}</div>;
}

// Tabs list (horizontal navigation)
export interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  showConnectors?: boolean;
  "aria-label"?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

export function TabsList({
  children,
  className = "",
  showConnectors = false,
  "aria-label": ariaLabel,
  onKeyDown,
}: TabsListProps) {
  const childArray = React.Children.toArray(children);

  if (!showConnectors) {
    return (
      <div
        className={cn("flex items-center gap-2", className)}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn("flex items-center", className)}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {childArray.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {index < childArray.length - 1 && (
            <div className="flex items-center mx-1">
              {/* Geometry copied verbatim from multi-rater's own connector
                  (SubmissionTabsClient.renderConnector): w-4 h-4 inside mx-1.
                  This prop had no callers, so it had drifted a size larger
                  than the sequence it is meant to look like. */}
              <svg
                className="w-4 h-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Individual tab trigger
export type TabStatus =
  | "complete"
  | "active"
  | "incomplete"
  | "locked"
  | "pending"
  | "upcoming"
  | "default";

export interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  status?: TabStatus;
  disabled?: boolean;
  className?: string;
  activeTab: string;
  onClick?: (value: string) => void;
  isLoading?: boolean;
  /** Optional count/badge element rendered after the label (e.g. unread count). */
  badge?: React.ReactNode;
  id?: string;
  "aria-controls"?: string;
  tabIndex?: number;
}

const statusConfig = {
  complete: {
    statusIcon: CheckCircle,
    statusIconColor: "text-green-600",
    bgColor: "bg-green-50",
    textColor: "text-green-900",
    hoverBg: "hover:bg-green-100",
    border: "border-green-200",
  },
  active: {
    statusIcon: null,
    statusIconColor: "",
    bgColor: "bg-navigator-primary",
    textColor: "text-white",
    hoverBg: "hover:bg-navigator-primary-dark",
    border: "border-navigator-primary",
  },
  pending: {
    statusIcon: AlertTriangle,
    statusIconColor: "text-amber-500",
    bgColor: "bg-amber-50",
    textColor: "text-navigator-primary",
    hoverBg: "hover:bg-amber-100",
    border: "border-amber-200",
  },
  incomplete: {
    statusIcon: AlertTriangle,
    statusIconColor: "text-amber-500",
    bgColor: "bg-amber-50",
    textColor: "text-navigator-primary",
    hoverBg: "hover:bg-amber-100",
    border: "border-amber-200",
  },
  locked: {
    statusIcon: Lock,
    statusIconColor: "text-gray-400",
    bgColor: "bg-gray-100",
    textColor: "text-gray-500",
    hoverBg: "",
    border: "border-gray-200",
  },
  // `locked` without the padlock. A step that simply hasn't happened yet is not
  // the same as one the user may not open: the padlock reads as "you lack
  // permission". Same greys so a read-only sequence still matches the tab bars
  // everywhere else. Pair it with `disabled` — unlike `locked` it does not
  // imply non-interactivity on its own.
  upcoming: {
    statusIcon: null,
    statusIconColor: "",
    bgColor: "bg-gray-100",
    textColor: "text-gray-500",
    hoverBg: "",
    border: "border-gray-200",
  },
  default: {
    statusIcon: null,
    statusIconColor: "",
    bgColor: "bg-white",
    textColor: "text-navigator-primary",
    hoverBg: "hover:bg-navigator-accent-light",
    border: "border-navigator-primary",
  },
};

export function TabsTrigger({
  value,
  children,
  status = "incomplete",
  disabled = false,
  className = "",
  activeTab,
  onClick,
  isLoading = false,
  badge,
  id,
  "aria-controls": ariaControls,
  tabIndex,
}: TabsTriggerProps) {
  const isActive = activeTab === value;
  const isDisabled = disabled || status === "locked";

  const handleClick = () => {
    if (!isDisabled && onClick) {
      onClick(value);
    }
  };

  // Calculate all visual state in one go to avoid intermediate renders
  const visualState = React.useMemo(() => {
    const isPendingActive = status === "pending" && isActive;
    const displayStatus = isPendingActive ? "active" : status;
    const config = statusConfig[displayStatus];

    return {
      config,
      statusIcon: config.statusIcon,
    };
  }, [status, isActive]);

  // Responsive sizing — compact pill below 1920px, full pill at 1920px+.
  const iconClass = "w-4 h-4 min-[1920px]:w-5 min-[1920px]:h-5";
  const sizingClass =
    "gap-1.5 px-3 py-1.5 text-xs " +
    "min-[1920px]:gap-3 min-[1920px]:py-[7px] min-[1920px]:text-base";

  const StatusIcon = visualState.statusIcon;
  const showStatusIcon = StatusIcon && !isActive;

  return (
    <button
      role="tab"
      id={id}
      aria-controls={ariaControls}
      aria-selected={isActive}
      aria-disabled={isDisabled}
      tabIndex={tabIndex}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        "flex items-center rounded-lg font-medium border",
        sizingClass,
        visualState.config.bgColor,
        visualState.config.textColor,
        !isDisabled && visualState.config.hoverBg,
        visualState.config.border,
        isDisabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
        isActive ? "shadow-md" : "shadow-sm",
        className,
      )}
    >
      {isLoading ? (
        <Loader2 className={cn(iconClass, "animate-spin")} />
      ) : (
        showStatusIcon && (
          <StatusIcon
            className={cn(iconClass, visualState.config.statusIconColor)}
          />
        )
      )}
      <span>{children}</span>
      {badge}
    </button>
  );
}

// Tab content panel
export interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  activeTab: string;
  id?: string;
  "aria-labelledby"?: string;
  tabIndex?: number;
}

export function TabsContent({
  value,
  children,
  className = "",
  activeTab,
  id,
  "aria-labelledby": ariaLabelledBy,
  tabIndex,
}: TabsContentProps) {
  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={ariaLabelledBy}
      tabIndex={tabIndex}
      className={cn("animate-in fade-in duration-200", className)}
    >
      {children}
    </div>
  );
}
